import {
  U21Chip,
  U21InfoItem,
  U21LoadError,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { formatDatetime } from 'app/shared/utils/date';
import { MAX_RISK } from 'app/modules/riskRatings/constants';
import {
  ALL_RISK_METRIC_DETAILS_MAP,
  RISK_PARAMETERS_MAP,
} from 'app/modules/riskRatings/components/riskParameters';
import {
  getCustomLabelFromParameterArgs,
  getParameterArgsFromRiskModel,
  isCustomDataMetric,
} from 'app/modules/riskRatings/helpers';
import { RiskMetricDetails } from 'app/modules/riskRatings/models';
import { keyPathToLabel } from 'app/modules/dataSettings/utils';
import { round } from 'lodash';
import styled from 'styled-components';
import {
  EntityLatestRiskScore,
  RiskModelSummary,
} from 'app/modules/riskRatings/responses';
import { useMemo } from 'react';
import { useFetchActiveRiskModel } from 'app/modules/riskRatings/queries/useFetchActiveRiskModel';
import { useSelector } from 'react-redux';
import { selectDataSettingsById } from 'app/modules/dataSettings/selectors';

interface Props {
  latestRiskScore: EntityLatestRiskScore;
  riskModelSummary: Pick<RiskModelSummary, 'title'>;
}

export const RiskScoreModelDetails = (props: Props) => {
  const { latestRiskScore, riskModelSummary } = props;
  const { score, source, updated_at: updatedAt } = latestRiskScore;
  const snapshot =
    latestRiskScore.source === 'MANUAL'
      ? latestRiskScore.override.model_snapshot
      : latestRiskScore.snapshot;

  const customDataSettingsById = useSelector(selectDataSettingsById);

  const { data: activeRiskModel, isError, refetch } = useFetchActiveRiskModel();

  const riskParameterArgsMap = useMemo(
    () =>
      getParameterArgsFromRiskModel(activeRiskModel)[
        snapshot.entity_segment_id ?? ''
      ] ?? {},
    [activeRiskModel, snapshot.entity_segment_id],
  );

  const defaultHighMatch = useMemo(
    () =>
      snapshot.trace.find((parameter) =>
        Boolean(
          parameter.parameter_trace.risk_metric_trace.risk_mapping_trace
            ?.default_high,
        ),
      ),
    [snapshot],
  );

  if (isError) {
    return (
      <U21LoadError label="active risk model" onTryAgain={() => refetch()} />
    );
  }

  return (
    <U21Spacer spacing={2}>
      <U21Spacer>
        <U21InfoItem label="Risk model">{riskModelSummary.title}</U21InfoItem>
        <U21InfoItem label="Segment">
          {snapshot.entity_segment_title}
        </U21InfoItem>
        <U21InfoItem label="Ran at">
          {formatDatetime(
            latestRiskScore.source === 'MANUAL'
              ? latestRiskScore.override.model_score_refreshed_at
              : updatedAt,
          )}
        </U21InfoItem>
      </U21Spacer>
      <U21Spacer>
        <StyledGrid>
          <U21Typography variant="subtitle2">Variable</U21Typography>
          <U21Typography variant="subtitle2">Value</U21Typography>
          <U21Typography variant="subtitle2">Risk</U21Typography>
          <U21Typography variant="subtitle2">Weight</U21Typography>
          <U21Typography variant="subtitle2">Points</U21Typography>
          {snapshot.trace.map(
            ({ parameter, parameter_trace: parameterTrace, weight }) => {
              const isDefaultHigh = defaultHighMatch?.parameter === parameter;
              if (defaultHighMatch && !isDefaultHigh) {
                // Only show the default high match (if present)
                return null;
              }

              const key = parameter;

              // Format Parameter
              const parameterScore = isDefaultHigh
                ? MAX_RISK
                : parameterTrace.parameter_risk_score || 0;
              let parameterName =
                RISK_PARAMETERS_MAP[parameter || '']?.label ?? parameter;

              // Use the custom parameter name if available
              const customParameterName = getCustomLabelFromParameterArgs(
                riskParameterArgsMap[parameterTrace?.parameter_id ?? ''],
              );
              if (customParameterName) {
                parameterName = customParameterName;
              }

              // Format Metric
              const metricConfig: RiskMetricDetails | undefined =
                ALL_RISK_METRIC_DETAILS_MAP[parameterTrace.risk_metric || ''];
              let metricName =
                metricConfig?.label ?? parameterTrace.risk_metric;
              if (
                parameterTrace.risk_metric &&
                isCustomDataMetric(parameterTrace.risk_metric)
              ) {
                const customDataMetricConfig =
                  customDataSettingsById[
                    parameterTrace.risk_metric_tracking_id || ''
                  ];
                if (customDataMetricConfig) {
                  metricName = keyPathToLabel(customDataMetricConfig);
                }
              }

              const shortMetricValue =
                metricConfig?.shortFormatValue?.(
                  parameterTrace.risk_metric_trace.raw_value,
                ) ??
                parameterTrace.risk_metric_trace.raw_value ??
                'Unknown';

              return [
                <div key={key}>
                  <U21Typography tooltip={parameterName} variant="body2">
                    {metricName}
                  </U21Typography>
                </div>,
                <div key={`${key}_value`}>
                  <U21Typography variant="body2">
                    {shortMetricValue}
                  </U21Typography>
                </div>,
                <div key={`${key}_score`}>
                  <U21Typography variant="body2">
                    {parameterScore}
                  </U21Typography>
                </div>,
                <div key={`${key}_weight`}>
                  <U21Typography variant="body2">
                    {round(weight * 100, 2)}%
                  </U21Typography>
                </div>,
                <div key={`${key}_impact`}>
                  {isDefaultHigh ? (
                    <U21Chip color="error">Default High</U21Chip>
                  ) : (
                    <U21Typography variant="body2">
                      {round(weight * parameterScore, 1)}
                    </U21Typography>
                  )}
                </div>,
              ];
            },
          )}
        </StyledGrid>
        <StyledFinalScore>
          <U21Typography variant="subtitle1">Total Score</U21Typography>
          <U21Typography variant="subtitle1">
            {Math.round(
              source === 'MANUAL'
                ? latestRiskScore.override.model_score
                : score,
            )}
          </U21Typography>
        </StyledFinalScore>
      </U21Spacer>
    </U21Spacer>
  );
};
const StyledGrid = styled.div`
  display: grid;
  gap: 8px 16px;
  grid-template-columns: 3fr 1fr 1fr 1fr 1fr;
  width: 100%;
`;

const StyledFinalScore = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-right: ${({ theme }) => theme.spacing(5.5)};
`;
